import { useEffect, useRef, useState } from "react";
import { Copy, FolderOpen, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";

type LibraryActionsMenuProps = {
  onLibraries?: () => void;
  onDuplicate: () => void;
  onEdit: () => void;
  onDelete: () => void;
  disabled?: boolean;
  align?: "left" | "right";
};

export function LibraryActionsMenu({
  onLibraries,
  onDuplicate,
  onEdit,
  onDelete,
  disabled = false,
  align = "right",
}: LibraryActionsMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    window.addEventListener("mousedown", onPointerDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
        className="inline-flex items-center justify-center rounded-md border border-slate-300 p-2 text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-white/20 dark:text-zinc-200 dark:hover:bg-white/5"
        aria-label="Library actions"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <MoreHorizontal className="h-4 w-4" aria-hidden />
      </button>
      {open ? (
        <div
          role="menu"
          className={cn(
            "absolute z-50 mt-1 w-44 origin-top rounded-md border border-slate-200 bg-white py-1 shadow-lg dark:border-white/10 dark:bg-zinc-900",
            align === "right" ? "right-0" : "left-0",
          )}
        >
          {onLibraries ? (
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 dark:text-zinc-100 dark:hover:bg-white/5"
              onClick={() => {
                setOpen(false);
                onLibraries();
              }}
            >
              <FolderOpen className="h-4 w-4" aria-hidden />
              Libraries
            </button>
          ) : null}
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 dark:text-zinc-100 dark:hover:bg-white/5"
            onClick={() => {
              setOpen(false);
              onDuplicate();
            }}
          >
            <Copy className="h-4 w-4" aria-hidden />
            Duplicate
          </button>
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-800 hover:bg-slate-50 dark:text-zinc-100 dark:hover:bg-white/5"
            onClick={() => {
              setOpen(false);
              onEdit();
            }}
          >
            <Pencil className="h-4 w-4" aria-hidden />
            Edit
          </button>
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-950/40"
            onClick={() => {
              setOpen(false);
              onDelete();
            }}
          >
            <Trash2 className="h-4 w-4" aria-hidden />
            Delete
          </button>
        </div>
      ) : null}
    </div>
  );
}
